import { useState, useRef, useCallback, useEffect } from 'react';
import { Sparkles, Heart, RotateCw, Play, Pause } from 'lucide-react';
import { COMPLIMENTS, COMPLIMENT_SECTION } from '@/config/valentineContent';

const allCompliments = COMPLIMENTS;
const TOTAL = allCompliments.length;
const TAG_COLORS = { poetic: '#9C27B0', sweet: '#E91E63', classic: '#F06292', deep: '#D81B60' };

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function ComplimentGenerator() {
  const [current, setCurrent] = useState(null);
  const [typed, setTyped] = useState('');
  const [animDir, setAnimDir] = useState('in');
  const [seenCount, setSeenCount] = useState(0);
  const [sparkles, setSparkles] = useState([]);
  const [autoPlay, setAutoPlay] = useState(false);
  const queueRef = useRef(shuffle(allCompliments));
  const indexRef = useRef(0);
  const typingRef = useRef(null);
  const sparkleIdRef = useRef(0);
  const autoTimerRef = useRef(null);

  const typeText = useCallback((text) => {
    setTyped('');
    if (typingRef.current) clearInterval(typingRef.current);
    let i = 0;
    typingRef.current = setInterval(() => {
      i++;
      setTyped(text.slice(0, i));
      if (i >= text.length) clearInterval(typingRef.current);
    }, 28);
  }, []);

  const spawnSparkles = useCallback(() => {
    const newSp = Array.from({ length: 6 }, () => ({
      id: ++sparkleIdRef.current,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 8 + 6,
      delay: Math.random() * 300,
    }));
    setSparkles(newSp);
    setTimeout(() => setSparkles([]), 800);
  }, []);

  const generate = useCallback(() => {
    if (seenCount >= TOTAL) return;

    if (indexRef.current >= queueRef.current.length) {
      queueRef.current = shuffle(allCompliments);
      indexRef.current = 0;
    }

    // Slide out old
    if (current) {
      setAnimDir('out');
      setTimeout(() => {
        const next = queueRef.current[indexRef.current];
        indexRef.current++;
        setCurrent(next);
        setSeenCount(prev => Math.min(prev + 1, TOTAL));
        setAnimDir('in');
        typeText(next.text);
        spawnSparkles();
      }, 300);
    } else {
      const next = queueRef.current[indexRef.current];
      indexRef.current++;
      setCurrent(next);
      setSeenCount(1);
      setAnimDir('in');
      typeText(next.text);
      spawnSparkles();
    }
  }, [current, typeText, spawnSparkles, seenCount]);

  // Auto-play — stop when all seen
  useEffect(() => {
    if (autoPlay && seenCount < TOTAL) {
      autoTimerRef.current = setInterval(generate, 4500);
    } else if (seenCount >= TOTAL && autoPlay) {
      setAutoPlay(false);
    }
    return () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); };
  }, [autoPlay, generate, seenCount]);

  const progress = seenCount / TOTAL;

  return (
    <section id="compliments" data-testid="compliment-section" className="section-romantic text-center">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-3">
          <p className="font-body text-[10px] tracking-widest uppercase mb-2" style={{ color: 'var(--accent-light)' }}>
            {COMPLIMENT_SECTION.sectionLabel}
          </p>
          <h2 className="font-heading text-2xl md:text-3xl" style={{ color: 'var(--text-primary)' }}>
            {COMPLIMENT_SECTION.heading}
          </h2>
        </div>

        {/* Progress arc + counter */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="relative w-10 h-10">
            <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
              <circle cx="20" cy="20" r="16" fill="none" strokeWidth="2.5" style={{ stroke: 'var(--accent-glow)' }} />
              <circle cx="20" cy="20" r="16" fill="none" strokeWidth="2.5" strokeLinecap="round"
                style={{
                  stroke: 'var(--accent-primary)',
                  strokeDasharray: `${progress * 100.5} 100.5`,
                  transition: 'stroke-dasharray 0.6s ease',
                }}
              />
            </svg>
            <Heart size={12} fill="var(--accent-primary)" strokeWidth={0}
              className="absolute inset-0 m-auto"
            />
          </div>
          <span className="font-body text-[10px]" style={{ color: 'var(--text-secondary)' }}>
            {seenCount} of {TOTAL}
          </span>
        </div>

        {/* Compliment card */}
        <div className="comp-card-wrapper relative mb-8" style={{ minHeight: 200 }}>
          {/* Decorative stacked cards behind */}
          <div className="comp-bg-card comp-bg-card-1" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--accent-glow)' }} />
          <div className="comp-bg-card comp-bg-card-2" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--accent-glow)' }} />

          {/* Main card */}
          <div
            className={`comp-card relative rounded-2xl p-6 sm:p-8 shadow-lg ${
              current ? (animDir === 'in' ? 'comp-slide-in' : 'comp-slide-out') : ''
            }`}
            style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--accent-glow)' }}
          >
            {/* Sparkle particles */}
            {sparkles.map(s => (
              <span
                key={s.id}
                className="comp-sparkle"
                style={{
                  left: `${s.x}%`,
                  top: `${s.y}%`,
                  fontSize: s.size,
                  animationDelay: `${s.delay}ms`,
                  color: 'var(--accent-light)',
                }}
              >
                ✦
              </span>
            ))}

            {!current ? (
              /* Initial empty state */
              <div className="flex flex-col items-center justify-center py-6 gap-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-glow)' }}>
                  <Sparkles size={24} style={{ color: 'var(--accent-primary)' }} />
                </div>
                <p className="font-body text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Tap below to receive your first compliment
                </p>
              </div>
            ) : (
              /* Compliment content */
              <div className="relative py-4">
                {/* Large decorative quote mark */}
                <span
                  className="absolute -top-1 left-2 font-heading text-6xl leading-none select-none opacity-10"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  &ldquo;
                </span>

                {/* Typed text */}
                <p
                  className="font-heading text-lg sm:text-xl md:text-2xl leading-relaxed px-4 min-h-[80px] flex items-center justify-center"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {typed}
                  <span className="comp-cursor" style={{ borderColor: 'var(--accent-primary)' }}>|</span>
                </p>

                {/* Tag badge */}
                <div className="flex items-center justify-center mt-4 gap-2">
                  <span
                    className="inline-block px-3 py-0.5 rounded-full font-body text-[10px] tracking-wider uppercase text-white"
                    style={{ backgroundColor: TAG_COLORS[current.tag] || 'var(--accent-primary)' }}
                  >
                    {current.tag}
                  </span>
                </div>

                {/* Closing quote */}
                <span
                  className="absolute -bottom-2 right-2 font-heading text-6xl leading-none select-none opacity-10"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  &rdquo;
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-3">
          <button
            data-testid="compliment-btn"
            onClick={() => { if (autoPlay) setAutoPlay(false); generate(); }}
            disabled={seenCount >= TOTAL}
            className="comp-btn inline-flex items-center gap-2 px-8 py-3 rounded-xl font-body text-sm font-medium text-white transition-all duration-300 hover:scale-105 active:scale-95"
            style={{
              background: seenCount >= TOTAL
                ? 'var(--accent-light)'
                : 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-light) 100%)',
              boxShadow: '0 8px 24px var(--accent-glow)',
              opacity: seenCount >= TOTAL ? 0.7 : 1,
              cursor: seenCount >= TOTAL ? 'default' : 'pointer',
            }}
          >
            <Sparkles size={16} />
            {seenCount >= TOTAL ? 'All compliments revealed!' : current ? 'Next compliment' : 'Give me a compliment'}
          </button>

          {/* Auto-play toggle */}
          {current && seenCount < TOTAL && (
            <button
              data-testid="compliment-autoplay-btn"
              onClick={() => setAutoPlay(prev => !prev)}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
              style={{
                backgroundColor: autoPlay ? 'var(--accent-primary)' : 'var(--accent-glow)',
                color: autoPlay ? '#fff' : 'var(--accent-primary)',
              }}
              title={autoPlay ? 'Pause auto-play' : 'Auto-play compliments'}
            >
              {autoPlay ? <Pause size={14} /> : <Play size={14} />}
            </button>
          )}
        </div>

        {/* History dots */}
        {seenCount > 0 && (
          <div className="flex items-center justify-center gap-1 mt-6 flex-wrap max-w-xs mx-auto">
            {Array.from({ length: Math.min(seenCount, TOTAL) }, (_, i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: i === seenCount - 1 ? 'var(--accent-primary)' : 'var(--accent-light)',
                  transform: i === seenCount - 1 ? 'scale(1.5)' : 'scale(1)',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
